/* THE VIDEO FRAMES, AS DELIVERY VIDEO.

   Mediabunny's CanvasSource hands the canvas to the encoder as it is, and the
   browser then labels the result FULL range (0-255), BT.709 matrix, sRGB
   transfer. That is correct and faithful in a browser, and wrong for almost
   everything else that opens an MP4: video editors and signage players expect
   LIMITED range (16-235) with the BT.709 curve, and many ignore the full-range
   flag outright. Premiere did — the darks came out crushed and the lights
   clipped, a grey of 32 showing as 18 and 224 as 242.

   So the conversion is done here, on purpose, and the file says what it is:
   limited range, BT.709 primaries, BT.709 matrix, BT.709 transfer. The RGB
   values of the canvas are treated as the gamma-encoded video values they
   already are — nothing is re-curved — which is what every other tool does
   with sRGB artwork going out as Rec.709 video.

   Exposed as a small object with the same add(timestamp, duration) the two
   exporters already call, so neither has to learn any of this. */

const COLOR_SPACE = { primaries: 'bt709', transfer: 'bt709', matrix: 'bt709', fullRange: false };

/**
 * RGBA -> planar I420, BT.709, limited range. Chroma is the mean of each 2x2
 * block. Integer arithmetic (the coefficients are BT.709's, scaled by 256), so
 * a 4K frame is a few tens of milliseconds, small beside the render and encode.
 * Transparent pixels are composited over black, which is what an alpha-less
 * H.264 picture has always meant here.
 *
 * @param {Uint8ClampedArray} rgba   w*h*4
 * @param {number} w  even
 * @param {number} h  even
 * @param {Uint8Array} out   w*h*3/2: the Y plane, then U, then V
 */
export function rgbaToI420Limited(rgba, w, h, out) {
  const ySize = w * h, cw = w >> 1, uOff = ySize, vOff = ySize + cw * (h >> 1);
  for (let y = 0; y < h; y += 2) {
    for (let x = 0; x < w; x += 2) {
      let rs = 0, gs = 0, bs = 0;
      for (let dy = 0; dy < 2; dy++) {
        for (let dx = 0; dx < 2; dx++) {
          const p = ((y + dy) * w + (x + dx)) * 4;
          let r = rgba[p], g = rgba[p + 1], b = rgba[p + 2];
          const a = rgba[p + 3];
          if (a !== 255) { r = (r * a / 255) | 0; g = (g * a / 255) | 0; b = (b * a / 255) | 0; }
          out[(y + dy) * w + (x + dx)] = ((47 * r + 157 * g + 16 * b + 128) >> 8) + 16;
          rs += r; gs += g; bs += b;
        }
      }
      const r = rs >> 2, g = gs >> 2, b = bs >> 2, ci = (y >> 1) * cw + (x >> 1);
      out[uOff + ci] = ((-26 * r - 87 * g + 112 * b + 128) >> 8) + 128;
      out[vOff + ci] = ((112 * r - 102 * g - 10 * b + 128) >> 8) + 128;
    }
  }
  return out;
}

/**
 * @param {object} mb      the imported 'mediabunny' module
 * @param {HTMLCanvasElement} canvas   the frame to encode, redrawn between adds
 * @returns {{ source: object, add: (timestamp: number, duration: number) => Promise<void> }}
 */
export function createVideoSource(mb, canvas) {
  const source = new mb.VideoSampleSource({ codec: 'avc', quality: mb.QUALITY_HIGH, keyFrameInterval: 2 });
  let buf = null;
  return {
    source,
    async add(timestamp, duration) {
      const w = canvas.width, h = canvas.height;
      if (!buf || buf.length !== w * h * 3 / 2) buf = new Uint8Array(w * h * 3 / 2);
      const rgba = canvas.getContext('2d').getImageData(0, 0, w, h).data;
      rgbaToI420Limited(rgba, w, h, buf);
      const sample = new mb.VideoSample(buf, {
        format: 'I420', codedWidth: w, codedHeight: h, timestamp, duration, colorSpace: COLOR_SPACE
      });
      try { await source.add(sample); } finally { sample.close(); }
    }
  };
}
